import { useState } from "react";

import { useAudienceAnalytics, useAudiencePlaythroughs } from "@/api/queries";
import type { AudienceMetricRow, PlaythroughRecord } from "@/api/types";
import { DataTable, type Column } from "@/components/DataTable";
import { Panel } from "@/components/Panel";
import { RemoteState } from "@/components/RemoteState";
import { SegmentControl } from "@/components/SegmentControl";
import { StatusLabel } from "@/components/StatusLabel";
import { useUiStore } from "@/store/ui";

type AudienceGroup = "storylet" | "story" | "project";

const GROUP_LABEL: Record<AudienceGroup, string> = {
  storylet: "按 Storylet",
  story: "按故事",
  project: "按项目",
};

/**
 * 观众洞察（`specs/15` §4 / §8 · `ADR-018` D4）。
 *
 * 编辑端只读视图：把玩家的**操作记录**与**聚合读数**（曝光 / 点击 / 选择 /
 * 点击率 / 选择率 / 兴趣）放在一起，供编剧回看真实选择。
 *
 * **只呈现、不判定**：口径只有一处（`specs/15` §4），前端不重算任何指标——
 * 尤其不自己算兴趣分（权重在 `packages/methodology` 的 HEU-012，后端算好回传）。
 * 分母为 0 的读数后端给 `null`，这里显示「—」而不是 0（`specs/12` §3.1）。
 *
 * @dataSource remote
 */
export function AudienceView() {
  const projectId = useUiStore((state) => state.projectId);
  const [groupBy, setGroupBy] = useState<AudienceGroup>("storylet");
  const analytics = useAudienceAnalytics(projectId, groupBy);
  const records = useAudiencePlaythroughs(projectId);

  const rows = analytics.data?.rows ?? [];
  const items = records.data?.items ?? [];

  const columns: Column<AudienceMetricRow>[] = [
    { key: "key", header: "维度", render: (row) => row.key },
    { key: "impressions", header: "曝光", render: (row) => row.impressions },
    { key: "clicks", header: "点击", render: (row) => row.clicks },
    { key: "selects", header: "选择", render: (row) => row.selects },
    { key: "click_rate", header: "点击率", render: (row) => formatRate(row.click_rate) },
    { key: "select_rate", header: "选择率", render: (row) => formatRate(row.select_rate) },
    { key: "avg_dwell", header: "平均停留", render: (row) => formatDuration(row.avg_dwell_ms) },
    { key: "interest", header: "兴趣", render: (row) => formatScore(row.interest_score) },
  ];

  return (
    <div className="page-scroll">
      <div className="page-width">
        <RemoteState queries={[analytics, records]} title="观众洞察加载失败">
          <header className="page-heading">
            <div>
              <span className="eyebrow">观众信号 · 编辑端</span>
              <h2>玩家的每个选择都真实落库，编剧看的是行为，不是猜测</h2>
              <p>
                读数直接从观众事件与游玩步骤聚合而来，不额外维护计数表；兴趣是停留时长归一
                与点击率的加权合成，权重来自方法论包，口径只有一处。
              </p>
            </div>
          </header>

          <Panel
            title="兴趣与转化"
            description="分母为 0 的读数显示为「—」——它不是 0，而是尚未有样本"
            actions={
              <SegmentControl<AudienceGroup>
                label="聚合维度"
                value={groupBy}
                onChange={setGroupBy}
                options={[
                  { value: "storylet", label: GROUP_LABEL.storylet },
                  { value: "story", label: GROUP_LABEL.story },
                  { value: "project", label: GROUP_LABEL.project },
                ]}
              />
            }
          >
            <DataTable
              columns={columns}
              rows={rows}
              rowKey={(row) => row.key}
              caption="按聚合维度的观众指标"
              empty={<p className="inline-note">还没有观众信号——有玩家走进故事后这里会亮起来。</p>}
            />
          </Panel>

          <Panel
            title="操作记录"
            description="最近的游玩与逐步选择；观众行为永不回写创作产物"
            bodyClassName="activity-list"
          >
            {items.length === 0 ? (
              <p className="inline-note">这个项目还没有游玩记录。</p>
            ) : (
              items.map((record) => <PlaythroughRow key={record.id} record={record} />)
            )}
          </Panel>
        </RemoteState>
      </div>
    </div>
  );
}

function PlaythroughRow({ record }: { record: PlaythroughRecord }) {
  return (
    <div className="activity-item">
      <span className="mini-mark">{record.status === "FINISHED" ? "✔" : "◇"}</span>
      <div>
        <p>
          {record.user_id} · {record.steps.length} 步
        </p>
        <small>
          {formatMoment(record.started_at)}
          {record.finished_at ? ` → ${formatMoment(record.finished_at)}` : ""}
        </small>
        <small style={{ display: "block" }}>
          {record.steps
            .map(
              (step) =>
                `${step.seq}. ${step.storylet_key}${step.chosen_key ? ` → ${step.chosen_key}` : "（继续）"}`,
            )
            .join("　")}
        </small>
      </div>
      <StatusLabel tone={record.status === "FINISHED" ? "success" : "info"}>
        {record.status}
      </StatusLabel>
    </div>
  );
}

/** 比率 → 百分数；`null`（未定义）显示「—」。 */
function formatRate(value: number | null): string {
  if (value === null) return "—";
  return `${(value * 100).toFixed(1)}%`;
}

/** 兴趣分 → 保留三位小数；`null` 显示「—」。 */
function formatScore(value: number | null): string {
  return value === null ? "—" : value.toFixed(3);
}

/** 毫秒 → 秒；`null` 显示「—」。 */
function formatDuration(value: number | null): string {
  return value === null ? "—" : `${(value / 1000).toFixed(1)}s`;
}

/** 时间展示到分钟（本地时区）。 */
function formatMoment(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  const pad = (part: number) => String(part).padStart(2, "0");
  return (
    `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}` +
    ` ${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`
  );
}
